
import { Component, Mixins, Prop, Ref, Watch } from 'vue-property-decorator'
import httpClient from '@/api/httpClient'
import { Globals, Waits } from '@/globals'
import Axios, { AxiosError, CancelTokenSource } from 'axios'
import StateMixin from '@/mixins/state'
import { Debounce } from 'vue-debounce-decorator'
import { VForm } from '@/types'
import consola from 'consola'

@Component({})
export default class AddInstanceDialog extends Mixins(StateMixin) {
  @Prop({ type: Boolean, required: true })
  public value!: boolean

  @Ref('form')
  readonly form!: VForm

  waits = Waits

  valid = true
  verifying = false
  verified = false
  error: any = null
  note: any = null

  rules = {
    required: (v: string) => !!v || this.$t('app.general.simple_form.error.required'),
    url: (v: string) => (this.validUrl(v)) || this.$t('app.general.simple_form.error.invalid_url')
  }

  /**
   * Validates a URL
   */
  validUrl (url: string) {
    try {
      this.buildUrl(url)
    } catch {
      return false
    }
    return true
  }

  /**
   * Builds the URL using the browsers URL class
   * Assume http:// if no protocol is given.
  */
  buildUrl (url: string) {
    if (
      !url.startsWith('http://') && !url.startsWith('https://')
    ) url = `http://${url}`
    return new URL(url)
  }

  timer = 0
  url = ''

  // Axios cancels.
  cancelSource: CancelTokenSource | undefined = undefined

  // Fetch cancels.
  controller: AbortController | undefined = undefined

  // Watch for valid url changes.
  @Watch('url')
  onUrlChange (value: string, oldVal: string) {
    if (value === oldVal) return
    if (this.valid) this.handleUrlChange(value)
  }

  @Debounce(750)
  async handleUrlChange (value: string) {
    if (this.valid) {
      this.verified = false
      this.error = null
      this.note = null
      this.verifying = true

      const url = this.buildUrl(value)

      // Handle cancelling axios requests.
      if (this.cancelSource !== undefined) {
        this.cancelSource.cancel('Cancelled due to new request.')
      }

      this.cancelSource = Axios.CancelToken.source()

      // Start by making a standard request. Maybe it's good?
      const request = await httpClient.get(
        url + 'server/info?t=' + new Date().getTime(), {
          withAuth: false,
          cancelToken: this.cancelSource.token
        })
        .then(() => {
          this.verified = true
          this.verifying = false
          return 'ok'
        })
        .catch((e: AxiosError) => {
          // If it failed because we cancelled, set ok and move on.
          if (Axios.isCancel(e)) {
            return 'ok'
          }

          // If it failed because of a 401, set ok and move on.
          if (e.response?.status === 401) {
            this.verified = true
            this.verifying = false
            return 'ok'
          }

          // If it failed with a network issue..
          if (e.request) return e.message

          // Otherwise pass along the error..
          this.error = e
          return 'ok'
        })

      // The initial request failed with a network issue..
      if (request !== 'ok') {
        // Handle cancelling fetch requests.
        if (this.controller !== undefined) {
          this.controller.abort()
        }
        this.controller = new AbortController()
        const { signal } = this.controller

        await fetch(url + 'server/info', { signal, mode: 'no-cors', cache: 'no-cache' })
          .then(() => {
            // likely a cors issue
            this.error = this.$t('app.endpoint.error.cors_error')
            this.note = this.$t('app.endpoint.error.cors_note', {
              url: Globals.DOCS_MULTIPLE_INSTANCES
            })
          })
          .catch(e => {
            // external host not reachable (fetch returns 'failed to fetch')
            consola.debug('Network Error', e, request)
            this.error = request
            this.note = this.$t('app.endpoint.error.cant_connect')
          })
          .finally(() => { this.verifying = false })
      }
    }
  }

  get helpTxt () {
    return this.$t('app.endpoint.msg.trouble', {
      url: Globals.DOCS_MULTIPLE_INSTANCES
    })
  }

  addInstance () {
    if (this.valid) {
      const url = this.buildUrl(this.url)
      const apiConfig = this.$filters.getApiUrls(url.toString())
      this.$emit('input', false)
      this.$emit('resolve', apiConfig)
    }
  }
}
